import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import {
  ConsultationDetailItem,
  ConsultationItem,
  ConsultationService,
  ConsultationTimelineEntry,
} from "service/consultation/service";
import {
  AUCTION_INQUIRY_STATUS_COLOR,
  AUCTION_INQUIRY_STATUS_LABEL,
  AuctionInquiryStatus,
  INQUIRY_CATEGORY_LABEL,
  PAYMENT_COLOR,
  PAYMENT_LABEL,
  SERVICE_CATEGORY_LABEL,
} from "constants/consultation";

const SERVICE_BORDER_COLOR: Record<ConsultationItem["service_category"], string> = {
  AUCTION_ALLCARE: "#3b82f6",
  NPL_ALLCARE: "#a855f7",
  BID_AGENT: "#f97316",
  RIGHTS_ANALYSIS: "#14b8a6",
  AUCTION_CONSULTATION: "#22c55e",
};

const PAYMENT_INFO_CATEGORIES: ConsultationItem["service_category"][] = [
  "AUCTION_ALLCARE",
  "BID_AGENT",
  "RIGHTS_ANALYSIS",
];

const LOGIN_TYPE_LABEL: Record<string, string> = {
  C: "일반 로그인",
  K: "카카오 로그인",
  N: "네이버 로그인",
  G: "구글 로그인",
};

const formatKST = (utc: string | null | undefined): string => {
  if (!utc) return "";
  const iso = utc.includes("T")
    ? utc.endsWith("Z")
      ? utc
      : `${utc}Z`
    : `${utc.replace(" ", "T")}Z`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return utc;
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${kst.getUTCFullYear()}-${pad(kst.getUTCMonth() + 1)}-${pad(kst.getUTCDate())} ${pad(
    kst.getUTCHours()
  )}:${pad(kst.getUTCMinutes())}`;
};

const formatKstDateOnly = (utc: string | null | undefined): string => {
  const s = formatKST(utc);
  return s ? s.slice(0, 10) : "";
};

const STATUS_ACTIONS: {
  status: AuctionInquiryStatus;
  label: string;
  variant: "outlined" | "filled";
}[] = [
  { status: AuctionInquiryStatus.REVIEWING, label: "검토중으로 변경", variant: "outlined" },
  { status: AuctionInquiryStatus.APPROVED, label: "승인으로 변경", variant: "outlined" },
  { status: AuctionInquiryStatus.IN_PROGRESS, label: "진행중으로 변경", variant: "outlined" },
  { status: AuctionInquiryStatus.COMPLETED, label: "완료처리", variant: "filled" },
  { status: AuctionInquiryStatus.CANCELLED, label: "취소처리", variant: "outlined" },
];

function ConsultationDetail(): JSX.Element | null {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [row, setRow] = useState<ConsultationDetailItem | null>(null);
  const [assigneeInput, setAssigneeInput] = useState("");
  const [memoInput, setMemoInput] = useState("");

  const fetchDetail = async () => {
    if (!id) return;
    try {
      const res = await ConsultationService.getDetail(id);
      setRow(res);
      setAssigneeInput(res?.assignee_name ?? "");
      setMemoInput(res?.memo ?? "");
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [id]);

  if (!row) return null;

  const serviceLabel = SERVICE_CATEGORY_LABEL[row.service_category] ?? row.service_category;
  const inquiryLabel = row.inquiry_category
    ? INQUIRY_CATEGORY_LABEL[row.inquiry_category] ?? row.inquiry_category
    : "-";
  const accent = SERVICE_BORDER_COLOR[row.service_category] ?? "#3b82f6";
  const statusLabel = AUCTION_INQUIRY_STATUS_LABEL[row.current_status] ?? row.current_status;
  const statusColor = AUCTION_INQUIRY_STATUS_COLOR[row.current_status] ?? "#6b7280";
  const showPaymentInfo = PAYMENT_INFO_CATEGORIES.includes(row.service_category);
  const loginLabel = LOGIN_TYPE_LABEL[row.user_login_type] ?? row.user_login_type ?? "-";

  const handleAssign = async () => {
    const name = assigneeInput.trim();
    if (!name) return;
    try {
      await ConsultationService.updateAssignee(row.ai_id, name);
      await fetchDetail();
    } catch (err) {
      console.log(err);
      alert("담당자 배정에 실패했습니다.");
    }
  };

  const handleSaveMemo = async () => {
    try {
      await ConsultationService.updateMemo(row.ai_id, memoInput);
      await fetchDetail();
    } catch (err) {
      console.log(err);
      alert("메모 저장에 실패했습니다.");
    }
  };

  const handleChangeStatus = async (next: AuctionInquiryStatus) => {
    if (next === row.current_status) return;
    try {
      await ConsultationService.updateStatus(row.ai_id, next);
      await fetchDetail();
    } catch (err) {
      console.log(err);
      alert("상태 변경에 실패했습니다.");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3} sx={{ fontFamily: "Pretendard" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            marginBottom: 24,
          }}
        >
          <button
            type="button"
            onClick={() => navigate("/management/consultation")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "8px 14px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              backgroundColor: "white",
              fontSize: 13,
              fontWeight: 500,
              fontFamily: "Pretendard",
              cursor: "pointer",
            }}
          >
            <Icon fontSize="small">arrow_back</Icon>
            목록으로
          </button>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <h2
              style={{
                margin: 0,
                fontSize: 20,
                fontWeight: 700,
                color: "#111827",
                fontFamily: "Pretendard",
              }}
            >
              {serviceLabel} 상세
            </h2>
            <span style={{ fontSize: 13, color: "#6b7280" }}>
              No. {row.ai_id} · {formatKstDateOnly(row.created_at)} 신청
            </span>
          </div>
        </div>

        <Card sx={{ borderLeft: `4px solid ${accent}`, mb: 2, p: "24px 28px" }}>
          <SectionTitle>고객 정보</SectionTitle>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              rowGap: 20,
              columnGap: 32,
              marginBottom: 20,
            }}
          >
            <Field label="No" value={String(row.ai_id)} />
            <Field
              label="서비스 유형"
              value={<span style={{ color: accent, fontWeight: 700 }}>{serviceLabel}</span>}
            />
            <Field label="고객명" value={row.user_name ?? "-"} />
            <Field label="연락처" value={row.user_tel?.trim() || "-"} />
            <Field label="이메일" value={row.user_email ?? "-"} />
            <Field label="로그인 방식" value={loginLabel} />
            <Field label="신청일시" value={formatKST(row.created_at)} />
            <Field label="상담분류" value={inquiryLabel} />
            {row.case_no && <Field label="사건번호" value={row.case_no} />}
            {row.item_no && <Field label="물건번호" value={row.item_no} />}
            {row.property_type && <Field label="부동산 종류" value={row.property_type} />}
          </div>
          <div style={{ marginTop: 4 }}>
            <div
              style={{
                fontSize: 13,
                color: "#6b7280",
                marginBottom: 8,
                fontFamily: "Pretendard",
              }}
            >
              문의내용
            </div>
            <div
              style={{
                padding: "16px 18px",
                backgroundColor: "#f9fafb",
                borderRadius: 8,
                border: "1px solid #f3f4f6",
                fontSize: 14,
                color: "#111827",
                whiteSpace: "pre-wrap",
                fontFamily: "Pretendard",
                minHeight: 56,
              }}
            >
              {row.content || "-"}
            </div>
          </div>
        </Card>

        {showPaymentInfo && (
          <Card sx={{ borderLeft: `4px solid #22c55e`, mb: 2, p: "24px 28px" }}>
            <SectionTitle>결제 정보</SectionTitle>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                rowGap: 20,
                columnGap: 32,
              }}
            >
              <Field label="선택 요금제" value={row.product_name ?? "-"} />
              <Field
                label="결제 금액"
                value={
                  row.product_price != null
                    ? `${Number(row.product_price).toLocaleString()}원`
                    : "-"
                }
              />
              <Field
                label="결제 상태"
                value={
                  <span
                    style={{
                      display: "inline-block",
                      padding: "4px 10px",
                      borderRadius: 6,
                      backgroundColor: `${PAYMENT_COLOR[row.payment_status]}1A`,
                      color: PAYMENT_COLOR[row.payment_status],
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {PAYMENT_LABEL[row.payment_status] ?? row.payment_status}
                  </span>
                }
              />
              <Field label="결제 방법" value="-" />
              <Field label="결제 일시" value="-" />
            </div>
          </Card>
        )}

        <Card sx={{ borderLeft: `4px solid #a855f7`, p: "24px 28px" }}>
          <SectionTitle>승인 및 처리 내역</SectionTitle>

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
            <span style={{ fontSize: 13, color: "#6b7280" }}>현재 처리 상태</span>
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: 6,
                border: `1px solid ${statusColor}`,
                color: statusColor,
                fontSize: 12,
                fontWeight: 600,
                backgroundColor: `${statusColor}10`,
              }}
            >
              {statusLabel}
            </span>
          </div>

          <SubLabel>담당자 배정</SubLabel>
          <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
            <input
              type="text"
              value={assigneeInput}
              onChange={(e) => setAssigneeInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAssign();
              }}
              placeholder="담당자 이름"
              style={{
                flex: "0 0 280px",
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: 8,
                fontSize: 14,
                fontFamily: "Pretendard",
              }}
            />
            <button
              type="button"
              onClick={handleAssign}
              style={{
                padding: "10px 22px",
                borderRadius: 8,
                border: "none",
                backgroundColor: "#2563eb",
                color: "white",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "Pretendard",
              }}
            >
              배정
            </button>
          </div>

          <SubLabel>관리자 메모</SubLabel>
          <textarea
            value={memoInput}
            onChange={(e) => setMemoInput(e.target.value)}
            placeholder="내부 메모를 입력하세요 (고객에게 노출되지 않습니다)"
            rows={3}
            style={{
              width: "100%",
              padding: "12px 14px",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              fontSize: 14,
              fontFamily: "Pretendard",
              resize: "vertical",
              marginBottom: 12,
              boxSizing: "border-box",
            }}
          />
          <button
            type="button"
            onClick={handleSaveMemo}
            style={{
              padding: "8px 22px",
              borderRadius: 8,
              border: "none",
              backgroundColor: "#1f2937",
              color: "white",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "Pretendard",
              marginBottom: 24,
            }}
          >
            저장
          </button>

          <SubLabel>상태 변경</SubLabel>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 24 }}>
            {STATUS_ACTIONS.map((action) => {
              const color = AUCTION_INQUIRY_STATUS_COLOR[action.status];
              const filled = action.variant === "filled";
              const disabled = action.status === row.current_status;
              return (
                <button
                  key={action.status}
                  type="button"
                  disabled={disabled}
                  onClick={() => handleChangeStatus(action.status)}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: filled ? "none" : `1px solid ${color}`,
                    backgroundColor: filled ? color : `${color}10`,
                    color: filled ? "white" : color,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.4 : 1,
                    fontFamily: "Pretendard",
                  }}
                >
                  {action.label}
                </button>
              );
            })}
          </div>

          <SubLabel>처리 이력</SubLabel>
          <Timeline items={row.timeline ?? []} />
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default ConsultationDetail;

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3
      style={{
        margin: 0,
        marginBottom: 20,
        fontSize: 16,
        fontWeight: 700,
        color: "#111827",
        fontFamily: "Pretendard",
      }}
    >
      {children}
    </h3>
  );
}

function SubLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 14,
        fontWeight: 600,
        color: "#111827",
        marginBottom: 8,
        fontFamily: "Pretendard",
      }}
    >
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 12, color: "#9ca3af", fontFamily: "Pretendard" }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 600, color: "#111827", fontFamily: "Pretendard" }}>
        {value}
      </span>
    </div>
  );
}

function Timeline({ items }: { items: ConsultationTimelineEntry[] }) {
  const sorted = useMemo(
    () => [...items].sort((a, b) => a.created_at.localeCompare(b.created_at)),
    [items]
  );

  if (sorted.length === 0) {
    return <div style={{ fontSize: 13, color: "#9ca3af" }}>이력이 없습니다.</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {sorted.map((it, idx) => {
        const dotColor =
          it.type === "STATUS" && it.value
            ? AUCTION_INQUIRY_STATUS_COLOR[it.value as AuctionInquiryStatus] ?? "#6b7280"
            : "#6b7280";
        return (
          <div key={idx} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                border: `2px solid ${dotColor}`,
                backgroundColor: "white",
                marginTop: 6,
                flex: "0 0 auto",
              }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontSize: 13, color: "#111827", fontFamily: "Pretendard" }}>
                {formatKST(it.created_at)}
              </span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13,
                  color: "#374151",
                  fontFamily: "Pretendard",
                  flexWrap: "wrap",
                }}
              >
                <span style={{ color: "#6b7280" }}>[system]</span>
                <TimelineBody entry={it} />
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TimelineBody({ entry }: { entry: ConsultationTimelineEntry }) {
  if (entry.type === "STATUS") {
    const status = entry.value as AuctionInquiryStatus;
    const color = AUCTION_INQUIRY_STATUS_COLOR[status] ?? "#6b7280";
    const label = AUCTION_INQUIRY_STATUS_LABEL[status] ?? entry.value ?? "-";
    return (
      <span
        style={{
          padding: "2px 8px",
          borderRadius: 6,
          border: `1px solid ${color}`,
          color,
          fontSize: 11,
          fontWeight: 600,
          backgroundColor: `${color}10`,
        }}
      >
        {label}
      </span>
    );
  }

  if (entry.type === "ASSIGN") {
    return <span>담당자 배정: {entry.value ?? "-"}</span>;
  }

  if (entry.type === "MEMO") {
    return <span>관리자 메모 {entry.value ? "수정" : "삭제"}</span>;
  }

  return <span>{entry.value ?? entry.type}</span>;
}
